'use client';

import { useState, useEffect, useRef } from 'react';
import { useReveal } from '@/components/marketing/engine/use-reveal';

/* v7 Infrastructure — a living vertical architecture. Signals flow through six stations.
   Story beat: CONVERGENCE. Active, not a compliance slide. */

interface Station {
  id: string;
  label: string;
  desc: string;
  proof: string;
  icon: React.ReactNode;
}

const STATIONS: Station[] = [
  { id: 'identity', label: 'Identity', desc: 'Who they are, confirmed before anything else.', proof: 'ID MATCHED', icon: <path d="M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M5 21a7 7 0 0 1 14 0" /> },
  { id: 'verify', label: 'Verification', desc: 'Every claim checked against real proof.', proof: 'CROSS-CHECKED', icon: <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z M9 12l2 2 4-4" /> },
  { id: 'assess', label: 'Assessment', desc: 'Skills tested live, under proctoring.', proof: 'SCORED 91', icon: <path d="M4 13l4 4 5-7 3 4 4-6 M3 21h18" /> },
  { id: 'interview', label: 'Interview', desc: 'Conversations captured as comparable signal.', proof: 'TRANSCRIPT SIGNED', icon: <path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z M5 11a7 7 0 0 0 14 0 M12 18v4" /> },
  { id: 'decision', label: 'Decision', desc: 'Evidence resolved into one clear call.', proof: 'RESOLVED', icon: <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M8 12l3 3 5-6" /> },
  { id: 'audit', label: 'Audit trail', desc: 'Every step, permanently logged and signed.', proof: 'SEALED · #88241', icon: <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M9 8h6 M9 12h6 M9 16h3" /> },
];

const AUDIT_FEED: string[] = [
  'PS-4471 · identity confirmed',
  'REQ-2231 · assessment sealed · 91',
  'PS-4471 · interview transcript signed',
  'AK-2290 · decision logged',
  'audit · block #88241 written',
];

interface AuditTickerProps {
  active: boolean;
}

function AuditTicker({ active }: AuditTickerProps) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => setIdx(i => (i + 1) % AUDIT_FEED.length), 2200);
    return () => clearInterval(iv);
  }, [active]);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '9px 16px', borderRadius: 'var(--r-full)',
      background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--green)', animation: 'spill-pulse 1.6s ease infinite' }}></span>
      <span className="mono" style={{ fontSize: '11px', color: 'var(--text-2)', minWidth: '230px', textAlign: 'left' }}>{AUDIT_FEED[idx]}</span>
    </div>
  );
}

export function InfrastructureSection() {
  const { ref, shown } = useReveal(0.12);
  const [fill, setFill] = useState(0);
  const pulseRef = useRef<HTMLSpanElement>(null);

  // ── One authoritative clock ──
  // A single pulse descends the spine; each station turns green the instant the
  // pulse crosses its node. Pill activation and pulse position are derived from
  // the SAME progress value, so they can never drift out of sync.
  useEffect(() => {
    if (!shown) return;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setFill(STATIONS.length); return; }

    const CYCLE = 6000;            // descent time
    const HOLD = 1300;             // all-lit "converged" beat before recharge
    const TOTAL = CYCLE + HOLD;
    const N = STATIONS.length;
    let raf = 0;
    let start = 0;
    let lastFill = -1;

    const tick = (t: number) => {
      if (!start) start = t;
      const elapsed = (t - start) % TOTAL;
      const p = Math.min(elapsed / CYCLE, 1);   // 0→1 descent, clamps through the hold

      // move the single pulse directly (no per-frame React re-render)
      const el = pulseRef.current;
      if (el) {
        el.style.top = `${p * 100}%`;
        el.style.opacity = String(p < 0.04 ? p / 0.04 : p > 0.97 ? Math.max(0, (1 - p) / 0.03) : 1);
      }

      // a station lights when the pulse has reached its node center
      let f = 0;
      for (let k = 0; k < N; k++) if (p >= (k + 0.5) / N) f = k + 1;
      if (p >= 1) f = N;
      if (f !== lastFill) { lastFill = f; setFill(f); }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [shown]);

  return (
    <section ref={ref} id="infra" className="section section--snug-top" style={{ background: 'var(--bg-veil)', overflow: 'hidden' }}>
      <div className="section-inner" style={{ position: 'relative', zIndex: 1, maxWidth: '900px' }}>
        <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 64px' }}>
          <span className="label" style={{ color: 'var(--headline-accent)', display: 'block', marginBottom: '16px', opacity: shown ? 1 : 0, transition: 'opacity .6s' }}>03 · CONVERGENCE</span>
          <h2 className="serif" style={{ fontSize: 'var(--h2)', lineHeight: 1.04, letterSpacing: '-0.01em',
            opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(14px)', transition: 'all .9s var(--ease) .1s' }}>
            Signals move through a system<br />that <span style={{ fontStyle: 'italic', color: 'var(--headline-accent)' }}>stays accountable.</span>
          </h2>
          <p style={{ fontSize: 'var(--body)', color: 'var(--text-2)', lineHeight: 1.6, marginTop: '16px', opacity: shown ? 1 : 0, transition: 'opacity .9s ease .25s' }}>
            Each candidate flows through one connected pipeline. Every stage
            verified, traceable, and never overwritten.
          </p>
        </div>

        {/* Vertical living architecture */}
        <div className="arch" style={{ position: 'relative', maxWidth: '760px', margin: '0 auto' }}>
          {/* central line */}
          <div className="arch-line" style={{ position: 'absolute', left: '50%', top: '20px', bottom: '20px', width: '2px', transform: 'translateX(-50%)',
            background: 'linear-gradient(to bottom, transparent, var(--border-hi) 6%, var(--border-hi) 94%, transparent)' }}>
            {/* single authoritative signal packet — its descent IS the clock */}
            {shown && (
              <span ref={pulseRef} style={{ position: 'absolute', left: '50%', top: '0%', width: '7px', height: '7px', marginLeft: '-3.5px', borderRadius: '50%',
                background: 'var(--indigo-light)', boxShadow: '0 0 12px var(--indigo-light), 0 0 4px #fff', opacity: 0, willChange: 'top, opacity' }}></span>
            )}
          </div>

          {STATIONS.map((s, i) => {
            const on = i < fill; const left = i % 2 === 0;
            const card = (
              <div className="arch-card" style={{ padding: '14px 18px', borderRadius: 'var(--r-md)',
                background: on ? 'linear-gradient(135deg, color-mix(in srgb, var(--green) 20%, var(--surface)), color-mix(in srgb, var(--green) 7%, var(--surface)))' : 'var(--surface)',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                border: `1px solid ${on ? 'color-mix(in srgb, var(--green) 34%, transparent)' : 'var(--border)'}`,
                boxShadow: on ? '0 0 26px -10px var(--green-spill)' : 'none',
                transition: 'background .55s var(--ease), border-color .55s var(--ease), box-shadow .55s var(--ease)',
                textAlign: left ? 'right' : 'left' as const }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: left ? 'flex-end' : 'flex-start', marginBottom: '5px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{s.label}</span>
                  <span className="mono" style={{ fontSize: '8px', letterSpacing: '0.04em', color: 'var(--green)', whiteSpace: 'nowrap',
                    opacity: on ? 1 : 0, transition: 'opacity .35s var(--ease)' }}>✓ {s.proof}</span>
                </div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            );
            return (
              <div key={s.id} className="arch-row" style={{ display: 'grid', gridTemplateColumns: '1fr 68px 1fr', alignItems: 'center', minHeight: '92px' }}>
                <div className="arch-side-l">{left ? card : <span></span>}</div>
                {/* node */}
                <div style={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
                  <div style={{ position: 'relative', width: '52px', height: '52px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: on ? 'var(--green-soft)' : 'var(--surface)', border: `1px solid ${on ? 'color-mix(in srgb, var(--green) 42%, transparent)' : 'var(--border)'}`,
                    boxShadow: on ? '0 0 24px var(--green-spill)' : 'none',
                    transform: on ? 'scale(1)' : 'scale(0.9)',
                    transition: 'background .5s var(--ease), border-color .5s var(--ease), box-shadow .5s var(--ease), transform .2s ease-out' }}>
                    {on && <span style={{ position: 'absolute', inset: '-1px', borderRadius: '50%', border: '1px solid var(--green)', animation: 'node-pulse 2.6s ease-in-out infinite' }}></span>}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={on ? 'var(--green)' : 'var(--text-3)'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'stroke .55s' }}>{s.icon}</svg>
                  </div>
                </div>
                <div className="arch-side-r">{!left ? card : <span></span>}</div>
              </div>
            );
          })}
        </div>

        {/* operational footer: live audit ticker */}
        <div style={{ textAlign: 'center', marginTop: '48px', opacity: shown ? 1 : 0, transition: 'opacity .9s ease .5s' }}>
          <AuditTicker active={shown} />
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .arch-row { grid-template-columns: 52px 1fr !important; gap: 16px; }
          .arch-line { left: 26px !important; }
          .arch-row > div:first-child { display: none !important; }
          .arch-row > div:nth-child(2) { justify-content: flex-start !important; }
          .arch-card { text-align: left !important; transform: none !important; }
          .arch-card > div { justify-content: flex-start !important; }
        }
      `}</style>
    </section>
  );
}

export default InfrastructureSection;
